/**
 * Suno 官方音乐接口
 *
 * POST /api/v1/generate
 * GET  /api/v1/generate/record-info?taskId=...
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var POLL_INTERVAL = 4000
var POLL_TIMEOUT = 10 * 60 * 1000

var SUBMIT_PATH = '/api/v1/generate'
var POLL_PATH = '/api/v1/generate/record-info'

function getSunoConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_SUNO_BASE_URL || 'https://api.sunoapi.org').replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_SUNO_API_KEY || '').trim()
  }
}

function createClient() {
  var cfg = getSunoConfig()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_SUNO_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_SUNO_API_KEY')
  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      Authorization: 'Bearer ' + cfg.apiKey,
      'Content-Type': 'application/json'
    }
  })
  instance.interceptors.response.use(
    function (res) { return res.data },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && (respData.msg || respData.message || respData.messge)) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

function ensureCode200(data, fallbackMsg) {
  var code = data && data.code
  if (Number(code) === 200) return
  var msg = (data && (data.msg || data.message)) || fallbackMsg || '请求失败'
  throw new Error(msg)
}

function downloadTrackAudioBlobs(tracks) {
  if (!tracks || !tracks.length) return Promise.resolve([])
  return Promise.all(
    tracks.map(function (t) {
      var u = t && t.audio_url ? String(t.audio_url) : ''
      if (!u) return Promise.resolve(null)
      return fetch(u, { mode: 'cors' })
        .then(function (res) {
          if (!res.ok) return null
          return res.blob()
        })
        .catch(function () {
          return null
        })
    })
  )
}

function normalizeTracks(sunoData) {
  var list = Array.isArray(sunoData) ? sunoData : []
  return list.map(function (item) {
    return {
      id: item && item.id ? item.id : '',
      audio_url: item && item.audioUrl ? item.audioUrl : '',
      image_url: item && item.imageUrl ? item.imageUrl : '',
      image_large_url: item && item.imageUrl ? item.imageUrl : '',
      prompt: item && item.prompt ? item.prompt : '',
      title: item && item.title ? item.title : '',
      tags: item && item.tags ? item.tags : '',
      duration: item && item.duration ? item.duration : 0
    }
  }).filter(function (item) { return !!item.audio_url })
}

export function submitVolcSunoMusicTask(params) {
  var client = createClient()
  var prompt = (params.prompt || params.lyrics || '').toString().trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))

  var body = {
    customMode: true,
    instrumental: params.make_instrumental === true,
    model: 'V4_5ALL',
    callBackUrl: 'https://api.example.com/callback',
    prompt: prompt,
    style: (params.tags || params.styleTags || '').toString().trim(),
    title: (params.title || params.songTitle || '').toString().trim(),
    styleWeight: 0.65,
    weirdnessConstraint: 0.65,
    audioWeight: 0.65
  }

  return client.post(SUBMIT_PATH, body).then(function (data) {
    ensureCode200(data, 'Suno 提交失败')
    var taskId = data && data.data && data.data.taskId ? String(data.data.taskId) : ''
    if (!taskId) return Promise.reject(new Error('官方接口未返回 taskId'))
    return { taskId: taskId }
  })
}

function parsePollStatus(data) {
  ensureCode200(data, 'Suno 查询失败')
  var d = data && data.data ? data.data : null
  var status = d && d.status ? String(d.status).toUpperCase() : ''
  var errMsg = (d && (d.errorMessage || d.errorCode)) ? String(d.errorMessage || d.errorCode) : ''
  var resp = d && d.response ? d.response : null
  var sunoData = resp && Array.isArray(resp.sunoData) ? resp.sunoData : []

  if (status === 'SUCCESS') {
    var tracks = normalizeTracks(sunoData)
    if (!tracks.length) return { status: 'failed', tracks: null, error: '任务成功但未返回音频链接' }
    return { status: 'completed', tracks: tracks, error: null }
  }
  if (status === 'FAILED' || status === 'FAIL' || status === 'ERROR' || status === 'CANCELLED') {
    return { status: 'failed', tracks: null, error: errMsg || '生成失败' }
  }
  return { status: 'processing', tracks: null, error: null }
}

export function pollVolcSunoMusicTask(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var client = createClient()

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(POLL_PATH, { params: { taskId: taskId } })
      .then(function (data) {
        if (stopped) return
        var parsed = parsePollStatus(data)

        if (parsed.status === 'completed') {
          var tr = parsed.tracks || []
          downloadTrackAudioBlobs(tr)
            .then(function (blobs) {
              if (stopped) return
              if (typeof callback === 'function') {
                callback(null, { status: 'completed', tracks: tr, audioBlobs: blobs, error: null })
              }
              stop()
            })
            .catch(function () {
              if (stopped) return
              if (typeof callback === 'function') {
                callback(null, { status: 'completed', tracks: tr, audioBlobs: null, error: null })
              }
              stop()
            })
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') callback(null, parsed)
          stop()
          return
        }

        if (typeof callback === 'function') callback(null, parsed)
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
          return
        }
        pollTimer = setTimeout(poll, pollInterval)
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
